import { DeleteOutlined, PlusOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { Spin, Tooltip, Typography } from 'antd';
import React from 'react';
import AuthenticatedAttachmentImage from '../../../../components/authenticated-attachment-image';
import { translations } from '../../../../config/translations';
import { showConfirm } from '../../../../helpers/NotificationHelper';
import { showError, showSuccess } from '../../../../helpers/NotificationHelper';
import {
    ApiException,
    AttachmentsClient,
    ToolDetailVm,
    ToolsClient,
    UpdateToolCommandOfTool,
} from '../../../../utils/api';

const MEDIA_TILE_PX = 112;

const shell: React.CSSProperties = {
    position: 'relative',
    width: MEDIA_TILE_PX,
    height: MEDIA_TILE_PX,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    flexShrink: 0,
};

const starStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 4,
    left: 4,
    cursor: 'pointer',
    fontSize: 18,
    zIndex: 2,
    lineHeight: 1,
    filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.9))',
};

const delStyle: React.CSSProperties = {
    position: 'absolute',
    top: 4,
    right: 4,
    color: '#ff4d4f',
    cursor: 'pointer',
    background: 'rgba(255,255,255,0.92)',
    padding: 4,
    borderRadius: 4,
    fontSize: 14,
    zIndex: 2,
    lineHeight: 1,
};

export interface ToolMultimediaSectionProps {
    tool: ToolDetailVm;
    canEdit: boolean;
    onToolUpdated: (tool: ToolDetailVm) => void;
}

/**
 * Gallery on tool details: upload/delete immediate; star saves thumbnail immediately (JJ-style).
 */
const ToolMultimediaSection: React.FC<ToolMultimediaSectionProps> = ({
    tool,
    canEdit,
    onToolUpdated,
}) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [busy, setBusy] = React.useState(false);

    const multimedia = tool.attachments?.filter((a) => a.isMultimedia === true) ?? [];
    const coverId = tool.coverAttachmentId ?? undefined;

    const refreshTool = async (): Promise<void> => {
        const t = await new ToolsClient().getById(tool.id);
        onToolUpdated(t);
    };

    const pushUpdateThumbnail = async (thumbnailAttachmentId: number | null): Promise<void> => {
        const cmd = new UpdateToolCommandOfTool();
        cmd.id = tool.id;
        cmd.name = tool.name;
        cmd.description = tool.description;
        cmd.isArchived = tool.isArchived;
        cmd.thumbnailAttachmentId =
            thumbnailAttachmentId === null ? (null as any) : thumbnailAttachmentId;
        await new ToolsClient().update(tool.id, cmd);
        await refreshTool();
        showSuccess(translations.tools.thumbnailUpdated);
    };

    const onStarClick = async (attachmentId: number): Promise<void> => {
        if (!canEdit || busy) return;
        setBusy(true);
        try {
            const next =
                coverId != null && coverId === attachmentId ? null : attachmentId;
            await pushUpdateThumbnail(next);
        } catch (e) {
            if (e instanceof ApiException) {
                showError(e.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        } finally {
            setBusy(false);
        }
    };

    const onDeleteSaved = (attachmentId: number): void => {
        if (!canEdit || busy) return;
        showConfirm(
            () => {
                void (async () => {
                    setBusy(true);
                    try {
                        await new AttachmentsClient().delete(attachmentId);
                        await refreshTool();
                        showSuccess(translations.attachments.deleteSuccess);
                    } catch (e) {
                        if (e instanceof ApiException) {
                            showError(e.response);
                        } else {
                            showError(translations.general.errorDeletingData);
                        }
                    } finally {
                        setBusy(false);
                    }
                })();
            },
            translations.tools.deleteImageConfirm,
            translations.general.delete,
            true
        );
    };

    const uploadFiles = async (list: FileList | File[]): Promise<void> => {
        const files = Array.from(list);
        const toSend: File[] = [];
        let bad = false;
        for (const raw of files) {
            if (!raw.type.startsWith('image/')) {
                bad = true;
                continue;
            }
            toSend.push(raw);
        }
        if (bad) {
            showError(translations.general.wrongFileType);
        }
        if (!toSend.length || !canEdit) return;

        setBusy(true);
        try {
            const client = new AttachmentsClient();
            for (const raw of toSend) {
                await client.create(
                    { data: raw, fileName: raw.name },
                    '',
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    tool.id,
                    true
                );
            }
            await refreshTool();
            showSuccess(translations.tools.multimediaUploaded);
        } catch (e) {
            if (e instanceof ApiException) {
                showError(e.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        } finally {
            setBusy(false);
        }
    };

    const openPicker = (): void => inputRef.current?.click();

    if (!canEdit) {
        return multimedia.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {multimedia.map((a) => (
                    <div key={a.id} style={shell}>
                        <AuthenticatedAttachmentImage
                            attachmentId={a.id}
                            alt={a.fileName}
                            width={MEDIA_TILE_PX}
                            height={MEDIA_TILE_PX}
                        />
                        {coverId === a.id && (
                            <StarFilled
                                style={{
                                    ...starStyle,
                                    cursor: 'default',
                                    color: '#faad14',
                                }}
                            />
                        )}
                    </div>
                ))}
            </div>
        ) : (
            <span>{translations.general.noData}</span>
        );
    }

    return (
        <Spin spinning={busy}>
            <div>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    disabled={busy}
                    onChange={(e) => {
                        const { files } = e.target;
                        if (files?.length) {
                            void uploadFiles(files);
                        }
                        e.target.value = '';
                    }}
                />
                <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}
                >
                    {multimedia.map((a) => (
                        <div key={a.id} style={shell}>
                            <AuthenticatedAttachmentImage
                                attachmentId={a.id}
                                alt={a.fileName}
                                width={MEDIA_TILE_PX}
                                height={MEDIA_TILE_PX}
                            />
                            <Tooltip title={translations.tools.listThumbnailStar}>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void onStarClick(a.id);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            void onStarClick(a.id);
                                        }
                                    }}
                                    style={{ ...starStyle, opacity: busy ? 0.5 : 1 }}
                                >
                                    {coverId === a.id ? (
                                        <StarFilled style={{ color: '#faad14' }} />
                                    ) : (
                                        <StarOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                                    )}
                                </span>
                            </Tooltip>
                            <DeleteOutlined
                                onClick={() => onDeleteSaved(a.id)}
                                style={{ ...delStyle, opacity: busy ? 0.5 : 1 }}
                            />
                        </div>
                    ))}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => !busy && openPicker()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!busy) openPicker();
                            }
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!busy && e.dataTransfer.files?.length) {
                                void uploadFiles(e.dataTransfer.files);
                            }
                        }}
                        style={{
                            width: MEDIA_TILE_PX,
                            height: MEDIA_TILE_PX,
                            borderRadius: 8,
                            border: '1px dashed #d9d9d9',
                            background: '#fafafa',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            cursor: busy ? 'wait' : 'pointer',
                            color: 'rgba(0,0,0,0.45)',
                            flexShrink: 0,
                            opacity: busy ? 0.7 : 1,
                        }}
                    >
                        <PlusOutlined style={{ fontSize: 22 }} />
                        <Typography.Text
                            type="secondary"
                            style={{ fontSize: 11, textAlign: 'center', padding: '0 6px' }}
                        >
                            {translations.tools.addMediaCardHint}
                        </Typography.Text>
                    </div>
                </div>
                <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, display: 'block', marginTop: 8 }}
                >
                    {translations.tools.listThumbnailHint}
                </Typography.Text>
            </div>
        </Spin>
    );
};

export default ToolMultimediaSection;
